/*
 * Status at a glance (docs/ux-principles.md, "Color means something"; Ryan, 2026-10-21: "the environments are just
 * text and the difference is barely legible"). Two small indicators in the fixed status tones:
 *
 *  - EnvChip: one environment as its own chip, its icon and name and a status mark. Healthy is a green dot; down or
 *    failed turns the chip red with an alert; deploying or setting up is a blue spinner; commits waiting is an amber
 *    dot with their count; not checked yet is a hollow gray dot; locked is a lock. An environment that doesn't
 *    exist is a dashed, muted chip with a "+". The words are always there for screen readers and in the tooltip.
 *  - StatusLine: a status line in a row ("Backed up 3 hr. ago", "Expires in 9 days"), its icon in the status
 *    color before the words, so a list reads without reading.
 */
import type { ComponentType, ReactNode } from "react";
import { cx } from "../lib/cx";
import type { StatusTone } from "../lib/statusTone";
import { IconAlert, IconAlertCircle, IconCheckCircle, IconClock, IconInfo, IconLock, IconPlus, Spinner, type IconProps } from "./icons";

export type EnvChipState = "healthy" | "down" | "failed" | "deploying" | "setting_up" | "waiting" | "unknown" | "locked" | "deleting" | "missing";

const STATE_WORD: Record<EnvChipState, string> = {
  healthy: "online",
  down: "not answering",
  failed: "failed",
  deploying: "deploying",
  setting_up: "setting up",
  waiting: "commits waiting",
  unknown: "not checked yet",
  locked: "locked",
  deleting: "deleting",
  missing: "not added",
};

export function EnvChip({
  label,
  icon: Icon,
  state,
  count,
  title,
  compact,
  className,
}: {
  /** In a table: no icon, tighter. */
  compact?: boolean;
  /** "Dev", "Test", "Live" or an extra environment's name. */
  label: string;
  icon?: ComponentType<IconProps>;
  state: EnvChipState;
  /** Commits waiting to be deployed to it; shown as an amber count. */
  count?: number | null;
  /** More words for the tooltip ("Error 503", "2 commits ready to deploy"). */
  title?: string;
  className?: string;
}) {
  const bad = state === "down" || state === "failed";
  const missing = state === "missing";
  const words = `${label}: ${count && state === "waiting" ? `${count} ${count === 1 ? "commit" : "commits"} waiting` : STATE_WORD[state]}`;
  return (
    <span
      title={title ? `${words}. ${title}` : words}
      className={cx(
        "inline-flex shrink-0 items-center rounded-full text-[12px] leading-none font-medium whitespace-nowrap",
        compact ? "h-[22px] gap-1 px-2" : "h-6 gap-1.5 pr-2 pl-1.5",
        missing
          ? "text-faint shadow-none outline-1 -outline-offset-1 outline-line-strong outline-dashed"
          : bad
            ? "bg-bad-bg text-bad shadow-[inset_0_0_0_1px_var(--hx-bad-line)]"
            : "bg-surface text-fg shadow-[inset_0_0_0_1px_var(--hx-line-strong)]",
        className,
      )}
    >
      {missing ? <IconPlus size={12} className="shrink-0" aria-hidden="true" /> : Icon && !compact ? <Icon size={12} className={cx("shrink-0", bad ? "text-current" : "text-dim")} aria-hidden="true" /> : null}
      <span aria-hidden="true">{label}</span>
      <span className="sr-only">{words}</span>
      <Mark state={state} count={count} />
    </span>
  );
}

function Mark({ state, count }: { state: EnvChipState; count?: number | null }) {
  switch (state) {
    case "healthy":
      return <i aria-hidden="true" className="block h-[7px] w-[7px] rounded-full bg-good-fill" />;
    case "down":
    case "failed":
      return <IconAlertCircle size={12} className="shrink-0" aria-hidden="true" />;
    case "deploying":
    case "setting_up":
    case "deleting":
      return <Spinner size={11} className="text-info" aria-hidden="true" />;
    case "waiting":
      return (
        <span aria-hidden="true" className="inline-flex items-center gap-1">
          <i className="block h-[7px] w-[7px] rounded-full bg-warn-fill" />
          {count ? <span className="rounded-full bg-warn-bg px-1 text-[10.5px] leading-[15px] font-semibold text-warn tabular-nums shadow-[inset_0_0_0_1px_var(--hx-warn-line)]">{count}</span> : null}
        </span>
      );
    case "locked":
      return <IconLock size={11} className="shrink-0 text-dim" aria-hidden="true" />;
    case "unknown":
      return <i aria-hidden="true" className="block h-[7px] w-[7px] rounded-full shadow-[inset_0_0_0_1.5px_var(--hx-faint)]" />;
    default:
      return null;
  }
}

const LINE_ICON: Record<StatusTone, ComponentType<IconProps> | null> = { good: IconCheckCircle, warn: IconAlert, bad: IconAlertCircle, info: IconClock, neutral: null };
const LINE_ICON_CLASS: Record<StatusTone, string> = { good: "text-good", warn: "text-warn", bad: "text-bad", info: "text-info", neutral: "text-faint" };
const LINE_TEXT: Record<StatusTone, string> = { good: "text-body", warn: "text-warn", bad: "font-medium text-bad", info: "text-body", neutral: "text-dim" };

/** Just the tone's icon in its color, for the start of a row whose words wrap. */
export function ToneIcon({ tone, icon, size = 15, className }: { tone: StatusTone; icon?: ComponentType<IconProps>; size?: number; className?: string }) {
  const Icon = icon ?? LINE_ICON[tone];
  return Icon ? <Icon size={size} className={cx("shrink-0", LINE_ICON_CLASS[tone], className)} aria-hidden="true" /> : null;
}

/** A status line in a row: the tone's icon in its color, then the words. Problems and warnings color the words too. */
export function StatusLine({ tone, icon, children, className, title }: { tone: StatusTone; icon?: ComponentType<IconProps>; children: ReactNode; className?: string; title?: string }) {
  const Icon = icon ?? LINE_ICON[tone] ?? (tone === "neutral" ? null : IconInfo);
  return (
    <span title={title} className={cx("inline-flex min-w-0 items-center gap-1.5", LINE_TEXT[tone], className)}>
      {Icon ? <Icon size={14} className={cx("shrink-0", LINE_ICON_CLASS[tone])} aria-hidden="true" /> : null}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}
